import React, { ReactElement, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from '@mui/material';

import { GenericModalData } from '../types/Modal';

import '../styles/modals/GenericModal.less';
import '../styles/modals/SharedModalStyles.less';

interface Props {
    modalData: GenericModalData | null;
}

export const colorMap: Record<string, 'warning' | 'error' | 'success'> = {
    YELLOW: 'warning',
    RED: 'error',
    GREEN: 'success'
};

const GenericModal = ({ modalData }: Props): ReactElement => {
    const closeButtonRef = useRef<HTMLButtonElement | null>(null);

    useEffect(() => {
        if (modalData) {
            window.addEventListener('keypress', keypressListener, true);
        } else {
            window.removeEventListener('keypress', keypressListener, true);
        }
    }, [modalData]);

    const keypressListener = (event: any) => handleButtonFocus(event, closeButtonRef);

    const handleButtonFocus = (e: any, closeButton: any) => {
        if ((e.key === 'r' || e.key === 'R') && closeButton) {
            closeButton.current?.focus();
        }
    };

    return (
        <>
            {modalData && (
                <Modal
                    isOpen={true}
                    ariaHideApp={false}
                    className="modal"
                    overlayClassName="modal-overlay"
                    onRequestClose={() => modalData.closeMe()}
                    shouldCloseOnOverlayClick={false}
                >
                    <div className="modal-header">
                        {modalData.header}
                        <FontAwesomeIcon
                            onClick={() => modalData.closeMe()}
                            className="close-icon"
                            icon={['far', 'times']}
                            id="dimgray"
                        />
                    </div>

                    <div className="modal-body">{modalData.body}</div>

                    <div className="modal-footer">
                        {modalData.type === 'CONFIRMATION' && (
                            <>
                                <Button
                                    variant="outlined"
                                    data-testid="no-btn"
                                    ref={closeButtonRef}
                                    onClick={() => modalData.closeMe()}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    data-testid="yes-btn"
                                    color={
                                        modalData.confirmBtnColor && colorMap[modalData.confirmBtnColor]
                                            ? colorMap[modalData.confirmBtnColor]
                                            : 'primary'
                                    }
                                    onClick={() => {
                                        if (modalData?.confirmAction) {
                                            modalData.confirmAction();
                                        }
                                        modalData.closeMe();
                                    }}
                                >
                                    Yes
                                </Button>
                            </>
                        )}
                        {modalData.type === 'INFORMATION' && (
                            <Button
                                variant="outlined"
                                data-testid="close-btn"
                                ref={closeButtonRef}
                                onClick={() => modalData.closeMe()}
                            >
                                Close
                            </Button>
                        )}
                    </div>
                </Modal>
            )}
        </>
    );
};

export default GenericModal;
